/**
 * Feedback Form Selector Component
 * Allows users to choose the type of feedback they want to provide
 */

import React from 'react';
import { cn } from '../../../lib/utils';
import { FeedbackType } from '../../../types/feedback';

interface FeedbackFormSelectorProps {
  onSelect: (type: FeedbackType) => void;
  availableTypes: FeedbackType[];
  className?: string;
}

const FeedbackFormSelector: React.FC<FeedbackFormSelectorProps> = ({
  onSelect,
  availableTypes,
  className
}) => {
  const feedbackOptions = [
    {
      type: 'page-comment' as const,
      title: 'Page Comment',
      description: 'Share thoughts about this page\'s content, design, or functionality',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      color: 'hover:bg-steel-50 hover:border-steel-300',
      iconColor: 'text-steel-600'
    },
    {
      type: 'bug-report' as const,
      title: 'Bug Report',
      description: 'Report an issue, error, or something that\'s not working correctly',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 2v4"/>
          <path d="M16 2v4"/>
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <path d="M3 10h18"/>
          <path d="M8 14h.01"/>
          <path d="M12 14h.01"/>
          <path d="M16 14h.01"/>
        </svg>
      ),
      color: 'hover:bg-coral-50 hover:border-coral-300',
      iconColor: 'text-coral-600'
    },
    {
      type: 'feature-request' as const,
      title: 'Feature Request',
      description: 'Suggest a new feature or improvement to make the platform better',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={3}/>
          <path d="M12 1v6m0 6v6"/>
          <path d="M21 12h-6m-6 0H3"/>
        </svg>
      ),
      color: 'hover:bg-sage-50 hover:border-sage-300',
      iconColor: 'text-sage-600'
    },
    {
      type: 'element-feedback' as const,
      title: 'Element Feedback',
      description: 'Comment on a specific element or section of this page',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 9l-4 4v3a2 2 0 0 0 2 2h3l4-4"/>
          <path d="M14 5l4 4-6.5 6.5a2 2 0 0 1-3-3L14 5z"/>
        </svg>
      ),
      color: 'hover:bg-amber-50 hover:border-amber-300',
      iconColor: 'text-amber-600'
    },
    {
      type: 'general' as const,
      title: 'General Feedback',
      description: 'Share general thoughts, questions, or any other feedback',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 12h.01"/>
          <path d="M12 12h.01"/>
          <path d="M16 12h.01"/>
          <path d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
      ),
      color: 'hover:bg-stone-50 hover:border-stone-300',
      iconColor: 'text-stone-600'
    }
  ].filter(option => availableTypes.includes(option.type));

  return (
    <div className={cn('space-y-4', className)}>
      <div className="text-center mb-6">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          What would you like to share?
        </h3>
        <p className="text-sm text-slate-600">
          Your feedback helps us improve the CLEAR platform for humanitarian response
        </p>
      </div>

      <div className="grid gap-3">
        {feedbackOptions.map((option) => (
          <button
            key={option.type}
            onClick={() => onSelect(option.type)}
            className={cn(
              'flex items-start gap-4 p-4 text-left border border-stone-200 rounded-lg',
              'transition-all duration-200 group',
              option.color
            )}
          >
            <div className={cn('flex-shrink-0 mt-1', option.iconColor)}>
              {option.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-800 group-hover:text-slate-900 mb-1">
                {option.title}
              </h4>
              <p className="text-sm text-slate-600 group-hover:text-slate-700 leading-relaxed">
                {option.description}
              </p>
            </div>
            <div className="flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-stone-200 text-center">
        <p className="text-xs text-slate-500 leading-relaxed">
          Your feedback is anonymous unless you choose to provide contact information. 
          All submissions help improve humanitarian data visualization and decision-making tools.
        </p>
      </div>
    </div>
  );
};

export default FeedbackFormSelector;